import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../features/auth/context/AuthContext';
import { usePlanEntitlements } from '../../features/billing/planEntitlements';
import { PermissionCodes, PermissionScopes } from '../../features/auth/permissions';
import { useAssetsModuleVisible } from '../../features/assets/assetsModule';
import { fieldOperationsModuleAvailable, useFieldOperationsModuleVisible } from '../../features/field-operations/fieldOperationsModule';
import { SidebarGlobalSearch } from './SidebarGlobalSearch';
import { SidebarIcon, SidebarIconName } from './SidebarIcon';
import { SidebarLayoutEditor } from './SidebarLayoutEditor';
import { readSidebarMode, resolveSidebarMode, SidebarMode, sidebarModeForPath, writeSidebarMode } from './sidebarMode';
import {
  addSidebarGroup,
  addSidebarLink,
  applySidebarLayout,
  isSidebarLayoutCustomized,
  DEFAULT_SIDEBAR_LAYOUT,
  moveSidebarEntryTo,
  removeSidebarGroup,
  removeSidebarLink,
  renameSidebarGroup,
  setSidebarItemHidden,
  SidebarEntry,
  SidebarGroupEntry,
  SidebarLayout,
  SidebarLayoutLink,
} from './sidebarLayout';
import { useSidebarLayout } from './useSidebarLayout';
import { useWorkspaceNavigationSettings } from './useWorkspaceNavigationSettings';
import { accessibleWorkspaceNavItems, WorkspaceNavAccess, WorkspaceNavItem } from './workspaceNavItems';
import { useWorkspaceSetupPending } from '../../features/workspace-setup/useWorkspaceSetupPending';

type SidebarProps = {
  collapsed?: boolean;
  onToggle?: () => void;
};

type NavGroupId = 'conversations' | 'admin';

type NavGroupToggleProps<TGroupId extends string> = {
  groupId: TGroupId;
  label: string;
  icon: SidebarIconName;
  isExpanded: boolean;
  isActive: boolean;
  collapsed: boolean;
  onToggle: (groupId: TGroupId) => void;
};

const NavGroupToggle = <TGroupId extends string>({
  groupId,
  label,
  icon,
  isExpanded,
  isActive,
  collapsed,
  onToggle,
}: NavGroupToggleProps<TGroupId>) => (
  <button
    type="button"
    className={`nav-item nav-group-toggle ${isActive ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
    onClick={() => onToggle(groupId)}
    aria-expanded={isExpanded}
    title={collapsed ? label : undefined}
  >
    <div className="nav-item-inner">
      <div className="nav-left">
        <span className="nav-icon" aria-hidden><SidebarIcon name={icon} /></span>
        <span className="nav-label">{label}</span>
      </div>
      <span className={`nav-chevron ${isExpanded ? 'nav-chevron-expanded' : ''}`} aria-hidden>›</span>
    </div>
  </button>
);

const isGroupActive = (
  groupId: NavGroupId,
  pathname: string,
): boolean => {
  switch (groupId) {
    case 'conversations':
      return pathname.startsWith('/conversations') || pathname.startsWith('/calls');
    case 'admin':
      return (pathname.startsWith('/admin') && !pathname.startsWith('/admin/connectors'))
        || pathname.startsWith('/members');
    default:
      return false;
  }
};

const getExpandedGroupsForLocation = (pathname: string): Record<NavGroupId, boolean> => ({
  conversations: isGroupActive('conversations', pathname),
  admin: isGroupActive('admin', pathname),
});

const matchesRoute = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const isWorkspaceItemActive = (item: WorkspaceNavItem, pathname: string): boolean =>
  item.end ? pathname === item.to : matchesRoute(pathname, item.activePrefix ?? item.to);

const NavSubItemContent: React.FC<{ icon: SidebarIconName; label: string }> = ({ icon, label }) => (
  <span className="nav-sub-item-main">
    <span className="nav-sub-icon" aria-hidden><SidebarIcon name={icon} /></span>
    <span className="nav-sub-label">{label}</span>
  </span>
);

export const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggle }) => {
  const { t } = useTranslation(['nav', 'header', 'workspaces']);
  const { hasPermission, auth } = useAuth();
  const { hasFeature } = usePlanEntitlements();
  const location = useLocation();
  const tenantId = auth?.tenantId ?? null;
  const employeeId = auth?.employeeId ?? null;
  const username = auth?.username ?? null;
  const isAssetsModuleVisible = useAssetsModuleVisible();
  const isFieldOperationsModuleVisible = useFieldOperationsModuleVisible();

  const canViewContacts = hasPermission(PermissionScopes.contactsRead) && hasFeature('contacts');
  const setupPending = useWorkspaceSetupPending(auth?.tenantId);
  const canViewAssets = hasPermission(PermissionScopes.assetsRead)
    && (isAssetsModuleVisible || hasPermission(PermissionCodes.assetsUpdate));
  const canViewProducts = hasPermission(PermissionScopes.productsRead) && hasFeature('products');
  const canViewServices = hasPermission(PermissionScopes.servicesRead) && hasFeature('services');
  const canViewInvoices = hasPermission(PermissionScopes.invoicesRead) && hasFeature('invoices');
  const canViewPayments = hasPermission(PermissionScopes.paymentsRead) && hasFeature('invoices');
  const canViewConversations = hasPermission(PermissionScopes.conversationsRead) && hasFeature('inbox');
  const canViewCalls = hasPermission(PermissionScopes.conversationsRead) && hasFeature('calls');
  const canViewCalendar = hasPermission(PermissionScopes.calendarRead) && hasFeature('calendar');
  const canViewRostering = hasPermission(PermissionScopes.rosteringRead);
  const canViewFieldOperations = fieldOperationsModuleAvailable
    && ((isFieldOperationsModuleVisible && hasPermission(PermissionScopes.fieldOperationsCheckIn))
      || hasPermission(PermissionScopes.fieldOperationsSettings));
  const canViewConsultations = hasPermission(PermissionScopes.consultationsRead) && hasFeature('consultations');
  const canViewTasks = hasPermission(PermissionScopes.tasksRead) && hasFeature('tasks');
  const canViewReminderEngine = hasPermission(PermissionScopes.remindersRead);
  const canViewKnowledge = hasPermission(PermissionScopes.knowledgeRead) && hasFeature('knowledgeBase');
  const canViewAiOrchestrators = hasPermission(PermissionScopes.aiOrchestratorsRead) && hasFeature('aiEmployees');
  const canViewApiTools = hasPermission(PermissionScopes.apiToolsRead) && hasFeature('api');
  const canViewDataTools = hasPermission(PermissionCodes.knowledgeManage) && hasFeature('knowledgeBase');
  const canViewMcpTools = hasPermission(PermissionScopes.mcpConnectorsRead) && hasFeature('mcpTools');
  const canViewAiConfig = hasPermission(PermissionScopes.aiConfigRead) && hasFeature('aiEmployees');
  const canViewTools = canViewApiTools || canViewDataTools || canViewMcpTools || canViewAiOrchestrators || canViewAiConfig;
  const canViewAiActivity = hasPermission(PermissionCodes.aiActivityRead);
  const canViewDashboard = (hasPermission(PermissionScopes.conversationsRead) && hasFeature('basicAnalytics'))
    || canViewConsultations
    || canViewTasks;

  const canViewTeamMembers = hasPermission(PermissionScopes.employeesRead);
  const canViewCompany = hasPermission(PermissionScopes.companiesRead);
  const canViewConnectors = hasPermission(PermissionScopes.connectorsRead);
  const canViewAutomations = hasPermission(PermissionScopes.conversationsRead) && hasFeature('automations');
  const canViewRoles = hasPermission(PermissionScopes.rolesRead);
  const canViewSecurity = hasPermission(PermissionCodes.adminAccess);
  const canViewAdminGroup = canViewTeamMembers || canViewCompany || canViewRoles || canViewSecurity;
  const assistantAvailable = canViewConversations
    || canViewCalls
    || canViewKnowledge
    || canViewAiOrchestrators
    || canViewTools
    || canViewAiConfig
    || canViewAiActivity
    || canViewAutomations
    || canViewConnectors;

  const workspaceAccess = React.useMemo<WorkspaceNavAccess>(() => ({
    dashboard: canViewDashboard,
    contacts: canViewContacts,
    assets: canViewAssets,
    products: canViewProducts,
    services: canViewServices,
    invoices: canViewInvoices,
    payments: canViewPayments,
    calendar: canViewCalendar,
    rostering: canViewRostering,
    fieldOperations: canViewFieldOperations,
    consultations: canViewConsultations,
    tasks: canViewTasks,
    reminderEngine: canViewReminderEngine,
  }), [
    canViewAssets,
    canViewCalendar,
    canViewConsultations,
    canViewContacts,
    canViewDashboard,
    canViewFieldOperations,
    canViewInvoices,
    canViewPayments,
    canViewProducts,
    canViewReminderEngine,
    canViewRostering,
    canViewServices,
    canViewTasks,
  ]);
  const workspaceItems = React.useMemo(() => accessibleWorkspaceNavItems(workspaceAccess), [workspaceAccess]);
  const workspaceAvailable = workspaceItems.length > 0;
  const showModeSwitch = assistantAvailable && workspaceAvailable;
  const [mode, setMode] = React.useState<SidebarMode>(() => resolveSidebarMode({
    pathname: location.pathname,
    stored: readSidebarMode(auth?.tenantId),
    assistantAvailable,
    workspaceAvailable,
  }));
  const [expandedGroups, setExpandedGroups] = React.useState<Record<NavGroupId, boolean>>(() =>
    getExpandedGroupsForLocation(location.pathname),
  );

  const { layout: personalLayout, updateLayout: updatePersonalLayout } = useSidebarLayout({ tenantId, employeeId, username });
  const { settings: navigationSettings } = useWorkspaceNavigationSettings(tenantId);
  const [editingLayout, setEditingLayout] = React.useState(false);
  const layoutScopeKey = `${tenantId ?? ''}|${employeeId ?? ''}|${username ?? ''}`;

  React.useEffect(() => {
    setEditingLayout(false);
  }, [layoutScopeKey]);

  // The workspace default (set by admins) is the starting point; a personal layout replaces it
  // entirely once the person customizes anything. Resetting goes back to the workspace default.
  const workspaceDefaultLayout = navigationSettings.defaultLayout;
  const hasPersonalLayout = isSidebarLayoutCustomized(personalLayout);
  const layout = hasPersonalLayout ? personalLayout : (workspaceDefaultLayout ?? personalLayout);
  const updateLayout = React.useCallback((updater: (current: SidebarLayout) => SidebarLayout) => {
    updatePersonalLayout(current => updater(
      isSidebarLayoutCustomized(current) ? current : (workspaceDefaultLayout ?? current),
    ));
  }, [updatePersonalLayout, workspaceDefaultLayout]);

  const labelOverrides = navigationSettings.labelOverrides;
  const workspaceLabel = React.useCallback(
    (item: WorkspaceNavItem) => labelOverrides[item.id] || t(item.labelKey),
    [labelOverrides, t],
  );

  const appliedLayout = React.useMemo(() => applySidebarLayout(workspaceItems, layout), [layout, workspaceItems]);
  const canCustomizeLayout = workspaceItems.length > 1;
  const [collapsedCustomGroups, setCollapsedCustomGroups] = React.useState<Record<string, boolean>>({});
  const toggleCustomGroup = React.useCallback((groupId: string) => {
    setCollapsedCustomGroups(current => ({ ...current, [groupId]: !current[groupId] }));
  }, []);

  React.useEffect(() => {
    // A group that holds the current page opens itself, like the built-in groups do.
    const activeGroupIds = appliedLayout.visible
      .filter((entry): entry is SidebarGroupEntry<WorkspaceNavItem> => entry.kind === 'group')
      .filter(group => group.children.some(child => child.kind === 'item' && isWorkspaceItemActive(child.item, location.pathname)))
      .map(group => group.id);
    if (activeGroupIds.length === 0) {
      return;
    }

    setCollapsedCustomGroups(current => {
      if (!activeGroupIds.some(id => current[id])) {
        return current;
      }

      const next = { ...current };
      for (const id of activeGroupIds) {
        delete next[id];
      }
      return next;
    });
  }, [appliedLayout.visible, location.pathname]);

  React.useEffect(() => {
    if (editingLayout && (mode !== 'workspace' || collapsed || !canCustomizeLayout)) {
      setEditingLayout(false);
    }
  }, [canCustomizeLayout, collapsed, editingLayout, mode]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'nav-item active' : 'nav-item';
  const isAiKnowledgePath = location.pathname.startsWith('/ai/knowledge') || location.pathname === '/knowledge';
  const isAiToolsPath = location.pathname.startsWith('/ai/tools')
    || location.pathname.startsWith('/ai/subagents')
    || location.pathname.startsWith('/ai/orchestrators');
  const isAiConfigPath = location.pathname.startsWith('/ai/config') || location.pathname === '/settings/ai';
  const isAiActivityPath = location.pathname.startsWith('/ai/activity');
  const isConversationsPath = location.pathname.startsWith('/conversations');
  const conversationView = new URLSearchParams(location.search).get('view');

  const toggleNavGroup = React.useCallback((groupId: NavGroupId) => {
    setExpandedGroups(current => ({
      ...current,
      [groupId]: !current[groupId],
    }));
  }, []);

  const selectMode = (next: SidebarMode) => {
    setMode(next);
    writeSidebarMode(tenantId, next);
  };

  const pathSyncKey = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (assistantAvailable && !workspaceAvailable) {
      setMode('assistant');
      return;
    }

    if (workspaceAvailable && !assistantAvailable) {
      setMode('workspace');
      return;
    }

    const syncKey = `${tenantId ?? ''}|${location.pathname}`;
    if (pathSyncKey.current === syncKey) {
      return;
    }

    pathSyncKey.current = syncKey;
    const next = resolveSidebarMode({
      pathname: location.pathname,
      stored: readSidebarMode(tenantId),
      assistantAvailable,
      workspaceAvailable,
    });
    setMode(next);
    if (sidebarModeForPath(location.pathname)) {
      writeSidebarMode(tenantId, next);
    }
  }, [assistantAvailable, location.pathname, tenantId, workspaceAvailable]);

  React.useEffect(() => {
    const autoExpanded = getExpandedGroupsForLocation(location.pathname);
    setExpandedGroups(current => ({
      ...current,
      ...(autoExpanded.conversations ? { conversations: true } : {}),
      ...(autoExpanded.admin ? { admin: true } : {}),
    }));
  }, [location.pathname, location.search]);

  const renderWorkspaceItem = (item: WorkspaceNavItem) => {
    const label = workspaceLabel(item);
    return (
      <NavLink key={item.id} to={item.to} end={item.end} className={linkClass} title={collapsed ? label : undefined}>
        <div className="nav-item-inner">
          <div className="nav-left">
            <span className="nav-icon" aria-hidden><SidebarIcon name={item.icon} /></span>
            <span className="nav-label">{label}</span>
          </div>
        </div>
      </NavLink>
    );
  };

  const renderWorkspaceLink = (link: SidebarLayoutLink) => (
    <a
      key={link.id}
      className="nav-item nav-external-link"
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      title={collapsed ? link.label : link.url}
    >
      <div className="nav-item-inner">
        <div className="nav-left">
          <span className="nav-icon" aria-hidden><SidebarIcon name="external" /></span>
          <span className="nav-label">{link.label}</span>
        </div>
      </div>
    </a>
  );

  const renderCustomGroup = (group: SidebarGroupEntry<WorkspaceNavItem>) => {
    if (group.children.length === 0) {
      return null;
    }

    const label = group.group.label || t('untitledGroup');
    const isExpanded = !collapsedCustomGroups[group.id];
    const isActive = group.children.some(child => child.kind === 'item' && isWorkspaceItemActive(child.item, location.pathname));

    return (
      <div key={group.id} className={`nav-group ${isExpanded ? 'expanded' : 'collapsed'}`} title={collapsed ? label : undefined}>
        <NavGroupToggle
          groupId={group.id}
          label={label}
          icon="folder"
          isExpanded={isExpanded}
          isActive={isActive}
          collapsed={collapsed}
          onToggle={toggleCustomGroup}
        />

        {!collapsed ? (
        <div className="nav-sub-items" aria-hidden={!isExpanded}>
        <div className="nav-sub-items-inner">
        {group.children.map(child => (
          child.kind === 'item' ? (
            <NavLink
              key={child.id}
              to={child.item.to}
              end={child.item.end}
              className={() => (isWorkspaceItemActive(child.item, location.pathname) ? 'nav-sub-item active' : 'nav-sub-item')}
              tabIndex={isExpanded ? undefined : -1}
            >
              <NavSubItemContent icon={child.item.icon} label={workspaceLabel(child.item)} />
            </NavLink>
          ) : (
            <a
              key={child.id}
              className="nav-sub-item nav-external-link"
              href={child.link.url}
              target="_blank"
              rel="noopener noreferrer"
              title={child.link.url}
              tabIndex={isExpanded ? undefined : -1}
            >
              <NavSubItemContent icon="external" label={child.link.label} />
            </a>
          )
        ))}
        </div>
        </div>
        ) : null}
      </div>
    );
  };

  const renderWorkspaceEntry = (entry: SidebarEntry<WorkspaceNavItem>) => {
    switch (entry.kind) {
      case 'item':
        return renderWorkspaceItem(entry.item);
      case 'link':
        return renderWorkspaceLink(entry.link);
      case 'group':
        return renderCustomGroup(entry);
      default:
        return null;
    }
  };

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-brand">
        <div className="brand-logo">{t('brandInitials', { ns: 'header' })}</div>
        <div className="sidebar-brand-copy">
          <span className="sidebar-brand-name">{t('brandName', { ns: 'header' })}</span>
        </div>
      </div>
      <SidebarGlobalSearch collapsed={collapsed} />
      {showModeSwitch ? (
        <div className="sidebar-mode-switch" role="group" aria-label={t('sidebarMode')}>
          <button
            type="button"
            aria-pressed={mode === 'assistant'}
            aria-label={t('assistantMode')}
            title={t('assistantMode')}
            onClick={() => selectMode('assistant')}
          >
            <span className="sidebar-mode-icon" aria-hidden><SidebarIcon name="assistant" /></span>
            <span className="sidebar-mode-label">{t('assistantMode')}</span>
          </button>
          <button
            type="button"
            aria-pressed={mode === 'workspace'}
            aria-label={t('workspaceMode')}
            title={t('workspaceMode')}
            onClick={() => selectMode('workspace')}
          >
            <span className="sidebar-mode-icon" aria-hidden><SidebarIcon name="dashboard" /></span>
            <span className="sidebar-mode-label">{t('workspaceMode')}</span>
          </button>
        </div>
      ) : null}
      <nav className="sidebar-nav">
        {setupPending ? (
        <NavLink to="/setup" className={linkClass} title={collapsed ? t('setup.resume', { ns: 'workspaces' }) : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="assistant" /></span>
              <span className="nav-label">{t('setup.resume', { ns: 'workspaces' })}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {mode === 'workspace' && editingLayout ? (
          <SidebarLayoutEditor
            visible={appliedLayout.visible}
            hidden={appliedLayout.hidden}
            canReset={hasPersonalLayout}
            labelFor={workspaceLabel}
            resetLabel={workspaceDefaultLayout ? t('customizeResetToWorkspace') : undefined}
            onMove={(id, target) => updateLayout(current => moveSidebarEntryTo(workspaceItems, current, id, target))}
            onSetHidden={(id, hidden) => updateLayout(current => setSidebarItemHidden(workspaceItems, current, id, hidden))}
            onAddGroup={(id, label) => updateLayout(current => addSidebarGroup(workspaceItems, current, id, label))}
            onRenameGroup={(id, label) => updateLayout(current => renameSidebarGroup(current, id, label))}
            onRemoveGroup={id => updateLayout(current => removeSidebarGroup(workspaceItems, current, id))}
            onAddLink={(label, url) => updateLayout(current => addSidebarLink(workspaceItems, current, label, url))}
            onRemoveLink={id => updateLayout(current => removeSidebarLink(workspaceItems, current, id))}
            onReset={() => updatePersonalLayout(() => DEFAULT_SIDEBAR_LAYOUT)}
            onDone={() => setEditingLayout(false)}
          />
        ) : null}

        {mode === 'workspace' && !editingLayout ? appliedLayout.visible.map(renderWorkspaceEntry) : null}

        {mode === 'assistant' && canViewConversations ? (
        <div className={`nav-group ${expandedGroups.conversations ? 'expanded' : 'collapsed'}`} title={collapsed ? t('conversations') : undefined}>
          <NavGroupToggle
            groupId="conversations"
            label={t('conversations')}
            icon="conversations"
            isExpanded={expandedGroups.conversations}
            isActive={isGroupActive('conversations', location.pathname)}
            collapsed={collapsed}
            onToggle={toggleNavGroup}
          />

          {!collapsed ? (
          <div className="nav-sub-items" aria-hidden={!expandedGroups.conversations}>
          <div className="nav-sub-items-inner">
          <NavLink
            to="/conversations?view=all"
            className={() => (isConversationsPath && conversationView !== 'direct' && conversationView !== 'channels' && conversationView !== 'external' ? 'nav-sub-item active' : 'nav-sub-item')}
            title={collapsed ? t('allConversations') : undefined}
            tabIndex={expandedGroups.conversations ? undefined : -1}
          >
            <NavSubItemContent icon="allConversations" label={t('allConversations')} />
          </NavLink>

          <NavLink
            to="/conversations?view=direct"
            className={() => (isConversationsPath && conversationView === 'direct' ? 'nav-sub-item active' : 'nav-sub-item')}
            title={collapsed ? t('directMessages') : undefined}
            tabIndex={expandedGroups.conversations ? undefined : -1}
          >
            <NavSubItemContent icon="directMessages" label={t('directMessages')} />
          </NavLink>

          <NavLink
            to="/conversations?view=channels"
            className={() => (isConversationsPath && conversationView === 'channels' ? 'nav-sub-item active' : 'nav-sub-item')}
            title={collapsed ? t('channels') : undefined}
            tabIndex={expandedGroups.conversations ? undefined : -1}
          >
            <NavSubItemContent icon="channels" label={t('channels')} />
          </NavLink>

          <NavLink
            to="/conversations?view=external"
            className={() => (isConversationsPath && conversationView === 'external' ? 'nav-sub-item active' : 'nav-sub-item')}
            title={collapsed ? t('external') : undefined}
            tabIndex={expandedGroups.conversations ? undefined : -1}
          >
            <NavSubItemContent icon="external" label={t('external')} />
          </NavLink>

          {canViewCalls ? (
          <NavLink
            to="/calls"
            className={({ isActive }) => (isActive ? 'nav-sub-item active' : 'nav-sub-item')}
            title={collapsed ? t('calls') : undefined}
            tabIndex={expandedGroups.conversations ? undefined : -1}
          >
            <NavSubItemContent icon="calls" label={t('calls')} />
          </NavLink>
          ) : null}
          </div>
          </div>
          ) : null}
        </div>
        ) : null}

        {mode === 'assistant' && canViewCalls && !canViewConversations ? (
        <NavLink to="/calls" className={linkClass} title={collapsed ? t('calls') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="calls" /></span>
              <span className="nav-label">{t('calls')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {mode === 'assistant' && canViewKnowledge ? (
        <NavLink to="/ai/knowledge" className={() => isAiKnowledgePath ? 'nav-item active' : 'nav-item'} title={collapsed ? t('knowledgeBase') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="knowledgeBase" /></span>
              <span className="nav-label">{t('knowledgeBase')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {mode === 'assistant' && canViewAiConfig ? (
        <NavLink to="/ai/config" className={() => isAiConfigPath ? 'nav-item active' : 'nav-item'} title={collapsed ? t('configuration') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="configuration" /></span>
              <span className="nav-label">{t('configuration')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {mode === 'assistant' && canViewTools ? (
        <NavLink to="/ai/tools" className={() => isAiToolsPath ? 'nav-item active' : 'nav-item'} title={collapsed ? t('tools') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="tools" /></span>
              <span className="nav-label">{t('tools')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {mode === 'assistant' && canViewAiActivity ? (
        <NavLink to="/ai/activity" className={() => isAiActivityPath ? 'nav-item active' : 'nav-item'} title={collapsed ? t('aiActivity') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="aiActivity" /></span>
              <span className="nav-label">{t('aiActivity')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {mode === 'assistant' && canViewAutomations ? (
        <NavLink to="/automations" className={linkClass} title={collapsed ? t('automations') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="automations" /></span>
              <span className="nav-label">{t('automations')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {mode === 'assistant' && canViewConnectors ? (
        <NavLink to="/admin/connectors" className={linkClass} title={collapsed ? t('connectors', { ns: 'admin' }) : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="connectors" /></span>
              <span className="nav-label">{t('connectors', { ns: 'admin' })}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewAdminGroup ? (
        <div className={`nav-group ${expandedGroups.admin ? 'expanded' : 'collapsed'}`} title={collapsed ? t('admin') : undefined}>
          <NavGroupToggle
            groupId="admin"
            label={t('admin')}
            icon="admin"
            isExpanded={expandedGroups.admin}
            isActive={isGroupActive('admin', location.pathname)}
            collapsed={collapsed}
            onToggle={toggleNavGroup}
          />

          {!collapsed ? (
          <div className="nav-sub-items" aria-hidden={!expandedGroups.admin}>
          <div className="nav-sub-items-inner">
          {canViewTeamMembers ? (
          <NavLink to="/members" className={({ isActive }) => isActive ? 'nav-sub-item active' : 'nav-sub-item'} title={collapsed ? t('teamMembers') : undefined} tabIndex={expandedGroups.admin ? undefined : -1}>
            <NavSubItemContent icon="teamMembers" label={t('teamMembers')} />
          </NavLink>
          ) : null}

          {canViewCompany ? (
          <NavLink to="/admin/companies" className={({ isActive }) => isActive ? 'nav-sub-item active' : 'nav-sub-item'} title={collapsed ? t('company') : undefined} tabIndex={expandedGroups.admin ? undefined : -1}>
            <NavSubItemContent icon="company" label={t('company')} />
          </NavLink>
          ) : null}

          {canViewRoles ? (
          <NavLink to="/admin/roles" className={({ isActive }) => isActive ? 'nav-sub-item active' : 'nav-sub-item'} title={collapsed ? t('roles') : undefined} tabIndex={expandedGroups.admin ? undefined : -1}>
            <NavSubItemContent icon="roles" label={t('roles')} />
          </NavLink>
          ) : null}

          {canViewSecurity ? (
          <NavLink to="/admin/security" className={({ isActive }) => isActive ? 'nav-sub-item active' : 'nav-sub-item'} title={collapsed ? t('security') : undefined} tabIndex={expandedGroups.admin ? undefined : -1}>
            <NavSubItemContent icon="security" label={t('security')} />
          </NavLink>
          ) : null}

          {canViewSecurity ? (
          <NavLink to="/admin/navigation" className={({ isActive }) => isActive ? 'nav-sub-item active' : 'nav-sub-item'} title={collapsed ? t('workspaceNavigation') : undefined} tabIndex={expandedGroups.admin ? undefined : -1}>
            <NavSubItemContent icon="customize" label={t('workspaceNavigation')} />
          </NavLink>
          ) : null}
          </div>
          </div>
          ) : null}
        </div>
        ) : null}

        {mode === 'workspace' && canCustomizeLayout && !collapsed && !editingLayout ? (
        <button
          type="button"
          className="nav-item nav-customize"
          onClick={() => setEditingLayout(true)}
        >
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="customize" /></span>
              <span className="nav-label">{t('customizeSidebar')}</span>
            </div>
          </div>
        </button>
        ) : null}
      </nav>
      <div className="sidebar-bottombar">
        <button
          className="sidebar-toggle"
          onClick={onToggle}
          title={collapsed ? t('expandSidebar') : t('collapseSidebar')}
        >
          {collapsed ? '»' : '«'}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
